import React, { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppLocale } from '../../i18n/localeUtils'
import { formatDateYYYYMMDD, formatInteger } from '../../packages/utils/numberFormatters'
import { FactsTable, FactsTableRow } from '../components/FactsTable'
import IssueIdLink from '../issues/IssuesIdLink'
import { Origin, WarrantEntity } from 'types/WarrantEntity'
import ConvertibleIdLink from '../convertibles/ConvertiblesIdLink'
import IssueTypeLabels, { KVLabel } from '../../components/entities/IssueTypeLabels'
import { IssueType } from '@finance-lab/utils/types'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'

const WarrantOrigin = ({ warrant }: { warrant: WarrantEntity }) => {
    const { t } = useTranslation()
    const { language: locale } = useAppLocale()

    const origins = warrant.origins

    const tableRows = []

    const wrapWithLink = (origin: Origin, label: ReactNode) => {
        if (origin.convertibleId) {
            return <ConvertibleIdLink convertible={origin}>{label}</ConvertibleIdLink>
        }
        return origin.type !== 'Guarantor fee issue' ? <IssueIdLink issue={origin}>{label}</IssueIdLink> : label
    }

    const getWarrantLabel = (origin: Origin) => {
        const date = origin.subEndDate
            ? formatDateYYYYMMDD(origin.subEndDate, { locale })
            : formatDateYYYYMMDD(origin.publicationDate, { locale })

        const getLabel = (): ReactNode => {
            if (origin.convertibleId && origin.convertibleName) {
                return (
                    <Stack direction='row' spacing={1}>
                        <KVLabel /> <Typography>{origin.convertibleName}</Typography>
                    </Stack>
                )
            }

            if (origin.type) {
                return (
                    <>
                        <IssueTypeLabels issue={origin as { id: string; type: IssueType }} isSingleLabel />{' '}
                        {t(`entities.issueType.values.${origin.type}`)}
                    </>
                )
            }

            return origin.externalComment
        }

        return origin.issueId || origin.convertibleId ? (
            <span>
                {wrapWithLink(origin, getLabel())} {date}
            </span>
        ) : (
            <span>
                {getLabel()} {date}
            </span>
        )
    }

    const totalWarrants = origins.reduce((sum, origin) => {
        return sum + (origin?.numberOfWarrants || 0)
    }, 0)

    if (origins && origins.length > 0) {
        tableRows.push(
            <FactsTableRow label={t('pages.warrants.origin.label')}>
                {t('pages.warrants.origin.numberOfWarrants')}
            </FactsTableRow>,
        )

        {
            origins.map((origin) => {
                tableRows.push(
                    <FactsTableRow label={getWarrantLabel(origin)}>
                        {formatInteger(origin.numberOfWarrants, { locale })}
                    </FactsTableRow>,
                )
            })
        }

        if (origins.length > 1) {
            tableRows.push(
                <FactsTableRow label={t('site.table.total')}>{formatInteger(totalWarrants, { locale })}</FactsTableRow>,
            )
        }
    }

    const content = <FactsTable rows={tableRows} />

    return <div>{content}</div>
}

export default WarrantOrigin
